import {
    Box,
    Divider,
    Icon,
    Stack,
    Typography,
    useMediaQuery,
} from "@mui/material";
import useRequest from "api/useRequest";
import { webEventsBookingsCreate } from "app/WebEvent/actions";
import { AxiosResponse } from "axios";
import trans from "helpers/trans";
import { FC } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Button,
    LoadingButton,
    ModalActions,
    ModalContent,
    ModalTitle,
    RequestMessage,
} from "ui";
import GuestQuantity from "./GuestQuantity";
import { useBasket, useBasketDispatch } from "./basketContext";
import BasketReducerActions from "./enums/BasketReducerActions";
import OrdinaryUpsale from "./Upsale/OrdinaryUpsale";
import Tickets from "./Ticket/Tickets";
import { WebBookingModel } from "app/WebEvent/interfaces";

type BasketProps = {
    onClose: () => void;
    uuid: string;
};

const Basket: FC<BasketProps> = ({ onClose, uuid }) => {
    const basket = useBasket();
    const dispatch = useBasketDispatch();
    const navigate = useNavigate();

    const smallScreen = useMediaQuery((theme: any) =>
        theme.breakpoints.down("tablet"),
    );

    const {
        "general-currency": currency,
        "general-button_background": buttonBackground,
    } = useSelector((state: any) => state.web.settings.location_settings);

    const {
        isLoading: isLoadingBooking,
        message: bookingMessage,
        request: requestBooking,
        status: bookingStatus,
    } = useRequest();

    const handleBuy = () => {
        if (!basket.event) {
            return;
        }

        requestBooking(
            webEventsBookingsCreate(
                {
                    event_id: basket.event.id,
                    tickets: basket.tickets.reduce(
                        (acc, ticket) => {
                            for (let i = 0; i < ticket.quantity; ++i) {
                                acc.push({
                                    id: null,
                                    event_ticket_id: ticket.id,
                                    ticket_upsales: ticket.ticket_upsales.map(
                                        (upsale) => ({
                                            event_ticket_upsale_id: upsale.id,
                                            id: null,
                                            quantity: upsale.quantity,
                                        }),
                                    ),
                                });
                            }

                            return acc;
                        },
                        [] as WebBookingModel["tickets"],
                    ),
                    event_upsales: basket.upsales.map((upsale) => ({
                        event_upsale_id: upsale.id,
                        id: null,
                        quantity: upsale.quantity,
                    })),
                },
                (response: AxiosResponse) => {
                    if (response.status === 204) {
                        navigate("/basket");
                        onClose();
                    }
                },
            ),
        );
    };

    const renderUpsales = () => {
        if (basket.event?.upsales.length === 0) {
            return null;
        }

        return (
            <>
                <Divider />
                {basket.event?.upsales.map((upsale) => (
                    <OrdinaryUpsale key={upsale.id} upsale={upsale} />
                ))}
            </>
        );
    };

    if (basket.event === null) {
        return null;
    }

    return (
        <>
            <ModalTitle
                onClose={onClose}
                sx={{
                    pl: 2,
                    pt: 1,
                    pr: 1,
                    pb: { xs: 1.5, tablet: 0 },
                    ".MuiTypography-root": {
                        color: "#495366",
                        fontFamily: "'Lato', sans-serif",
                        fontSize: 18,
                        fontWeight: 700,
                    },
                }}
                modalActions={
                    smallScreen &&
                    basket.event?.age_limit && (
                        <Box
                            sx={{
                                display: "inline-flex",
                                justifyContent: "center",
                                alignItems: "start",
                                alignSelf: "start",
                                ml: "auto",
                            }}
                        >
                            <Box
                                sx={{
                                    background: "#fff",
                                    color: "#000",
                                    borderRadius: "50%",
                                    textAlign: "center",
                                    border: "2px solid red",
                                    height: 36,
                                    width: 36,
                                    margin: "2px",
                                    fontFamily: "'Noto Sans', sans-serif",
                                    fontWeight: 500,
                                    fontSize: 13,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {basket.event.age_limit}+
                            </Box>
                        </Box>
                    )
                }
            >
                {smallScreen && basket.event.title}
            </ModalTitle>
            <ModalContent
                sx={{
                    pt: 0,
                    px: { xs: 2, tablet: 4 },
                    display: "flex",
                    flexDirection: "column",
                    pb: 0,
                }}
            >
                <RequestMessage
                    message={bookingMessage}
                    status={bookingStatus}
                    sx={{
                        width: "100% !important",

                        "&.MuiPaper-root": {
                            height: "fit-content",
                            mb: 2,
                        },
                    }}
                />

                <Box>
                    <Typography
                        sx={{
                            fontFamily: "'Lato', sans-serif",
                            fontSize: { xs: 16, tablet: 20 },
                            color: { xs: "#495366", tablet: "#000" },
                            fontWeight: 700,
                            textAlign: "center",
                            mb: { xs: 1.5, tablet: 2.5 },
                        }}
                    >
                        {trans(
                            `event.webEvent.modal.ticketModal.title.chooseType`,
                        )}
                    </Typography>

                    <Stack
                        divider={
                            <Divider
                                orientation="horizontal"
                                flexItem
                                sx={{ my: 2 }}
                            />
                        }
                    >
                        <Stack spacing={1.25}>
                            <Typography
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    fontFamily: "'Noto Sans', sans-serif",
                                    fontSize: { xs: 14, tablet: 16 },
                                    fontWeight: 500,
                                    color: { xs: "#495366", tablet: "#02263A" },
                                }}
                            >
                                <Icon
                                    sx={{
                                        fontSize: { xs: 12, tablet: 16 },
                                        color: "#407BFF",
                                        mr: 1,
                                    }}
                                >
                                    circle
                                </Icon>
                                {basket.event.localDateTime}
                            </Typography>
                            {!smallScreen && (
                                <Box
                                    sx={{
                                        fontFamily: "'Lato', sans-serif",
                                        fontSize: 18,
                                        color: "#02263A",
                                        fontWeight: 700,
                                        textAlign: "left",
                                        alignItems: "center",
                                        display: "flex",
                                    }}
                                >
                                    {basket.event.title}
                                    {!smallScreen && basket.event.age_limit && (
                                        <Box
                                            sx={{
                                                display: "inline-flex",
                                                justifyContent: "center",
                                                alignItems: "start",
                                                alignSelf: "start",
                                                ml: 1,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    background: "#fff",
                                                    color: "#000",
                                                    borderRadius: "50%",
                                                    textAlign: "center",
                                                    border: "2px solid red",
                                                    height: 36,
                                                    width: 36,
                                                    margin: "2px",
                                                    fontFamily:
                                                        "'Noto Sans', sans-serif",
                                                    fontWeight: 500,
                                                    fontSize: 13,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                {basket.event.age_limit}+
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Stack>
                        {!basket.event.ticket_multiselection ? (
                            <GuestQuantity />
                        ) : (
                            <Box />
                        )}
                    </Stack>
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        minHeight: "90px",
                        overflowY: "auto",
                        overflowX: "hidden",
                        minWidth: "fit-content",
                        // mt: { xs: 2, tablet: 2.5 },
                        p: 0,
                        gap: { xs: 2, tablet: 2.5 },
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Tickets />

                    {renderUpsales()}
                </Box>
            </ModalContent>
            <ModalActions sx={{ pt: 2, px: 4, pb: 3.75 }}>
                <Box
                    display="flex"
                    width="100%"
                    textAlign="right"
                    flexDirection="column"
                    justifyContent="center"
                    sx={{
                        gap: { xs: 2, tablet: 3 },
                    }}
                >
                    <Divider sx={{ mt: 1 }} />
                    <Box
                        sx={{
                            display: "flex",
                            p: 0,
                            justifyContent: "space-between",
                            my: 0,
                            flexWrap: "wrap",
                            gap: 0.5,
                            flexDirection: "row",
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: {
                                    xs: "'Noto Sans', sans-serif",
                                    tablet: "'Lato', sans-serif",
                                },
                                fontSize: 18,
                                fontWeight: {
                                    xs: 500,
                                    tablet: 700,
                                },
                                color: "#495366",
                                textTransform: "uppercase",
                            }}
                        >
                            {trans("event.webEvent.modal.ticketModal.sum")}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: {
                                    xs: "'Noto Sans', sans-serif",
                                    tablet: "'Lato', sans-serif",
                                },
                                fontSize: 18,
                                fontWeight: {
                                    xs: 500,
                                    tablet: 700,
                                },
                                color: "#495366",
                            }}
                        >
                            {basket.totalPrice.price(currency, "left")}
                        </Typography>
                    </Box>
                    <Stack
                        direction="row"
                        sx={{
                            marginLeft: "0!important",
                            width: "100%",
                            gap: { xs: 1, tablet: 2 },
                            p: 0,
                        }}
                    >
                        <Button
                            variant="text"
                            onClick={() =>
                                dispatch?.({
                                    type: BasketReducerActions.CLEAR_EVENT,
                                })
                            }
                            sx={{
                                ":hover": {
                                    boxShadow: "none",
                                    background: "transparent",
                                },
                                fontSize: { xs: 18, tablet: 20 },
                                borderRadius: 1,
                                boxShadow: "none",
                                padding: 0,
                                background: "transparent",
                                height: 48,
                                color: "#777777",
                                textAlign: "center",
                                fontFamily: "'Source Sans 3', sans-serif",
                                fontWeight: 500,
                            }}
                        >
                            {trans("global.button.back")}
                        </Button>
                        <LoadingButton
                            onClick={handleBuy}
                            loading={isLoadingBooking}
                            disabled={
                                basket.event === null ||
                                basket.totalTickets === 0
                            }
                            sx={{
                                ":hover": {
                                    background: buttonBackground,
                                    boxShadow: "none",
                                },
                                background: buttonBackground,
                                fontFamily: "'Source Sans 3', sans-serif",
                                fontWeight: 500,
                                fontSize: { xs: 18, tablet: 20 },
                                borderRadius: 1,
                                boxShadow: "none",
                                padding: "0 16px",
                                height: 48,
                                display: "flex",
                                flex: 1,
                                m: "auto",
                                color: (theme) =>
                                    theme.palette.getContrastText(
                                        buttonBackground,
                                    ),
                            }}
                        >
                            {trans("global.button.next")}
                        </LoadingButton>
                    </Stack>
                </Box>
            </ModalActions>
        </>
    );
};

export default Basket;
